import React from 'react';
import ListViewBookingCount from '../../ListViewBookingCount';
import { Meta } from '../../../../styled components/Typography';
import { CardDetailValue } from './ListViewCardDetails.style';
import {
  bookingCellValue,
  formatCellValue,
  NO_VALUE,
  RESOURCE_NAME_COLUMN_KEY
} from '../../../../libs/listViewPresentation';
import type { ListViewBooking, ListViewColumn, TimeFormatSetting } from '../../../types';

interface ListViewCardDetailsProps {
  booking: ListViewBooking;
  /* The visible columns other than the one shown as the card's title */
  detailColumns: ListViewColumn[];
  /* The columns whose value changed since the previous fetch */
  changedColumnKeys: string[];
  dateFormatSetting: string;
  timeFormatSetting: TimeFormatSetting;
}

/* The column values of a booking, as the label/value pairs of a card. A column
   without a value for this booking is left out entirely - unless the value is
   what changed since the previous fetch: a comment that was cleared is still a
   change, and the row stays, with the em dash in place of the value, so the
   card has something to mark it on. */
const ListViewCardDetails = ({
  booking,
  detailColumns,
  changedColumnKeys,
  dateFormatSetting,
  timeFormatSetting
}: ListViewCardDetailsProps) => (
  <dl tw="mt-4 mb-0">
    {detailColumns.map((column) => {
      const value = formatCellValue(
        bookingCellValue(booking, column),
        column,
        dateFormatSetting,
        timeFormatSetting
      );
      const hasValue = value !== null && value !== '';
      const changed = changedColumnKeys.includes(column.key);
      if (!hasValue && !changed) return null;
      return (
        <div tw="flex mt-2" key={column.key}>
          <Meta as="dt" tw="shrink-0 w-1/3 mr-8">
            {column.label || column.key}
          </Meta>
          <CardDetailValue
            as="dd"
            $changed={changed}
            data-test-changed-field={changed || undefined}
          >
            {hasValue ? value : NO_VALUE}
          </CardDetailValue>
          {/* The count has no row of its own: it is shown beside the name it
              belongs to, wherever the card puts that - here on the cards
              titled with another column, and in the title on the rest (see
              ListViewCard) */}
          {column.key === RESOURCE_NAME_COLUMN_KEY && (
            <ListViewBookingCount booking={booking} />
          )}
        </div>
      );
    })}
  </dl>
);

export default ListViewCardDetails;
